/**
 * Past workout detail (clicked from history). Read-only view of a
 * completed/skipped session with its sets grouped by block.
 */

import { Icon } from '@k-os/ui';
import {
  useWorkoutSession,
  type ExerciseSetDto,
  type ExerciseType,
} from '../../../api/health';
import {
  DifficultyDot,
  EXERCISE_BLOCK_META,
  EXERCISE_TYPE_META,
  StatusPill,
  TypeChip,
} from './atoms';
import styles from './workouts.module.css';

const TYPE_KEYS = Object.keys(EXERCISE_TYPE_META) as ExerciseType[];
const BLOCK_ORDER = ['warmup', 'main', 'accessory', 'cooldown'] as const;

export function WorkoutDetail({
  sessionId,
  onBack,
}: {
  sessionId: string;
  onBack: () => void;
}) {
  const q = useWorkoutSession(sessionId);

  if (q.isLoading) {
    return <div style={{ color: 'var(--ink-3)' }}>Loading…</div>;
  }
  if (!q.data) {
    return <div style={{ color: 'var(--ink-3)' }}>Workout not found.</div>;
  }
  const session = q.data.workout;
  const sets = q.data.sets;

  // Group by exerciseName/exerciseId
  const grouped: Map<
    string,
    {
      exerciseName: string;
      block: string | null;
      sets: ExerciseSetDto[];
    }
  > = new Map();
  for (const s of sets.slice().sort((a, b) => a.setIndex - b.setIndex)) {
    const key = `${s.exerciseId ?? ''}::${s.exerciseName}::${s.block ?? ''}`;
    let g = grouped.get(key);
    if (!g) {
      g = { exerciseName: s.exerciseName, block: s.block, sets: [] };
      grouped.set(key, g);
    }
    g.sets.push(s);
  }

  const byBlock = BLOCK_ORDER.map((b) => ({
    block: b,
    items: [...grouped.values()].filter((g) => g.block === b),
  })).filter((g) => g.items.length > 0);

  const totalSets = sets.length;
  const doneSets = sets.filter((s) => s.status === 'completed').length;

  return (
    <div>
      <div className={styles.crumb}>
        <span className={styles.crumbLink} onClick={onBack}>
          History
        </span>
        <Icon name="chevron" size={11} />
        <span className={styles.crumbActive}>{session.name}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>
          {session.name}
        </h2>
        {isExerciseType(session.type) && <TypeChip type={session.type} />}
        <StatusPill status={session.status} />
      </div>
      <div style={{ fontSize: 14, color: 'var(--ink-3)', marginTop: 4 }}>
        {new Date(session.date).toLocaleDateString(undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          marginTop: 18,
          marginBottom: 22,
        }}
      >
        <Kpi label="Started" value={session.startTime ? new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'} />
        <Kpi label="Ended" value={session.endTime ? new Date(session.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'} />
        <Kpi label="Duration" value={session.durationMin ? `${session.durationMin} min` : '—'} />
        <Kpi label="Sets done" value={`${doneSets} / ${totalSets || 1}`} />
      </div>

      {session.notes && (
        <div
          style={{
            padding: 14,
            marginBottom: 18,
            fontStyle: 'italic',
            color: 'var(--ink-2)',
            borderLeft: '3px solid var(--accent)',
            background: 'var(--canvas)',
            borderRadius: 6,
          }}
        >
          "{session.notes}"
        </div>
      )}

      {session.status === 'skipped' && (
        <div
          style={{
            padding: 20,
            textAlign: 'center',
            color: 'var(--ink-3)',
            border: '1px solid var(--line)',
            borderRadius: 8,
          }}
        >
          <Icon name="x" size={20} />
          <div style={{ marginTop: 8 }}>This workout was skipped.</div>
        </div>
      )}

      {byBlock.map((g) => (
        <div key={g.block} style={{ marginBottom: 22 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
            {EXERCISE_BLOCK_META[g.block].name}{' '}
            <span style={{ fontFeatureSettings: "'tnum'", color: 'var(--ink-4)', fontWeight: 400, textTransform: 'none' }}>
              {g.items.length}
            </span>
          </h3>
          {g.items.map((ex, i) => {
            const allCompleted = ex.sets.every((s) => s.status === 'completed');
            const allSkipped = ex.sets.every((s) => s.status === 'skipped');
            const status = allCompleted ? 'completed' : allSkipped ? 'skipped' : 'partial';
            return (
              <div
                key={i}
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  background: 'var(--canvas)',
                  marginBottom: 10,
                  padding: '10px 14px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{ex.exerciseName}</span>
                  <StatusPill status={status} />
                </div>
                <div style={{ marginTop: 6 }}>
                  {ex.sets.map((s) => (
                    <SetSummary key={s.id} set={s} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
        <button type="button" className="kos-btn" onClick={onBack}>
          Back to history
        </button>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        background: 'var(--canvas)',
        padding: '10px 12px',
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, fontFeatureSettings: "'tnum'" }}>{value}</div>
    </div>
  );
}

function SetSummary({ set }: { set: ExerciseSetDto }) {
  if (set.status === 'skipped') {
    return (
      <div style={{ fontSize: 13, color: 'var(--ink-4)', padding: '2px 0' }}>
        {set.setIndex}. <span style={{ textDecoration: 'line-through' }}>skipped</span>
        {set.skipReason ? ` — ${set.skipReason}` : ''}
      </div>
    );
  }
  if (set.actual) {
    return (
      <div style={{ fontSize: 14, padding: '2px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--ink-3)' }}>did</span>
        <span style={{ fontFeatureSettings: "'tnum'" }}>{set.actual}</span>
        {set.difficulty != null && <DifficultyDot value={set.difficulty} />}
      </div>
    );
  }
  return (
    <div style={{ fontSize: 14, padding: '2px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: 'var(--ink-4)', width: 26 }}>{set.setIndex}.</span>
      <span style={{ fontFeatureSettings: "'tnum'" }}>
        {set.weight != null ? `${set.weight} kg` : 'BW'} × {set.reps ?? '—'} reps
      </span>
      {set.difficulty != null && <DifficultyDot value={set.difficulty} />}
    </div>
  );
}

function isExerciseType(v: string): v is ExerciseType {
  return TYPE_KEYS.includes(v as ExerciseType);
}
